<script>
export default {
  name: "FunnelBar",
  props: {
    segments: { type: Array, default: () => [] },
    loaded: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    completionRate: { type: String, default: "" }
  },
  computed: {
    total() { return this.segments.reduce((sum, s) => sum + (s.value || 0), 0); },
    hasData() { return this.total > 0; },
    isLowData() { return this.total > 0 && this.total < 10; },
    tiers() {
      if (!this.hasData) return [];
      const maxWidth = 100;
      return this.segments.map((seg, i) => {
        const pct = ((seg.value / this.total) * 100).toFixed(1);
        const width = Math.max(maxWidth - (i * (maxWidth / (this.segments.length + 1))), 30);
        return { ...seg, pct, width };
      });
    }
  }
};
</script>

<template>
  <div class="funnel-content">
    <div
      v-if="error"
      class="funnel-empty"
    >
      <i class="fa-solid fa-triangle-exclamation text-amber-400" />
      <p>Unable to load</p>
    </div>

    <template v-else-if="!loaded">
      <div
        v-for="i in 3"
        :key="i"
        class="skel funnel-tier-skel"
        :style="{ width: (100 - i * 20) + '%' }"
      />
    </template>

    <div
      v-else-if="!hasData"
      class="funnel-empty"
    >
      <p>Not enough data to report on.</p>
    </div>

    <template v-else>
      <div class="funnel-stack">
        <div
          v-for="tier in tiers"
          :key="tier.label"
          class="funnel-tier"
          :style="{ width: tier.width + '%', backgroundColor: tier.color }"
          :title="`${tier.label}: ${tier.value} out of ${total}`"
        >
          <span class="tier-label">{{ tier.label }}</span>
          <span class="tier-value">{{ tier.value }} <small>({{ tier.pct }}%)</small></span>
        </div>
      </div>
      <p
        v-if="isLowData"
        class="low-data"
      >
        Limited data — may not be representative
      </p>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.funnel-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; color: #6b7280; font-size: 1rem; gap: 4px; padding-top: 8px; }
.funnel-content { display: flex; flex-direction: column; flex: 1; justify-content: center; gap: 8px; padding: 8px 0 8px 16px; }

.funnel-stack { display: flex; flex-direction: column; align-items: center; gap: 6px; flex: 1; justify-content: center; }

.funnel-tier {
  height: 35px; border-radius: 6px; display: flex; align-items: center;
  justify-content: space-between; padding: 0 12px; gap: 8px;
  transition: width 0.6s ease-out; cursor: default; overflow: hidden;
  &:hover { filter: brightness(1.08); }
}

.tier-label { font-size: 0.8rem; font-weight: 600; color: white; text-shadow: 0 0 2px rgba(0,0,0,0.2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.tier-value { font-size: 0.8rem; font-weight: 700; color: white; text-shadow: 0 0 2px rgba(0,0,0,0.2); flex-shrink: 0; text-align: right; line-height: 1.2; small { font-weight: 400; opacity: 0.85; font-size: 0.6rem; } }

@media screen and (max-width: 1920px) {
  .tier-label { font-size: 0.65rem; }
  .tier-value { font-size: 0.65rem; }
}

@media screen and (max-width: 1366px) {
  .funnel-tier { flex-direction: column; justify-content: center; padding: 2px 6px; gap: 0; }
  .tier-label { font-size: 0.5rem; text-align: center; }
  .tier-value { font-size: 0.5rem; text-align: center; line-height: 1.1; }
}

.funnel-rate { text-align: center; padding-top: 4px; }
.rate-num { font-size: 1.1rem; font-weight: 800; color: #10be5a; }
.rate-label { font-size: 0.65rem; color: #6b7280; margin-left: 4px; }
.low-data { text-align: center; font-size: 0.6rem; color: #9ca3af; font-style: italic; }

.funnel-tier-skel { height: 44px; margin: 3px auto; }
.skel { background: #f3f4f6; border-radius: 4px; animation: shimmer 1.5s ease-in-out infinite; }
@keyframes shimmer { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

@media screen and (max-width: 1024px) {
  .funnel-content { overflow: hidden; }
  .funnel-stack { width: 100%; }
  .funnel-tier { height: 35px; }
}

@media screen and (max-width: 820px) {
  .funnel-content { overflow: hidden; }
  .funnel-stack { width: 100%; }
  .funnel-tier { height: 32px; }
}

@media print {
  .funnel-stack {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 6px !important;
  }
  .funnel-tier {
    height: 44px !important;
    border-radius: 6px !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    padding: 0 12px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .tier-label { font-size: 0.65rem !important; font-weight: 600 !important; color: white !important; }
  .tier-value { font-size: 0.65rem !important; font-weight: 700 !important; color: white !important; }
}
</style>
